import {
    GET_GSLA_FOR_WAREHOUSE,
    GET_GSLA_RECOMMENDATIONS_FOR_WAREHOUSE,
    PUBLISH_GSLA_FOR_WAREHOUSE,
    GET_USER_PERMISSIONS,
    GET_GSLA_FOR_MULTIPLE_WAREHOUSES,
    GET_GSLA_RECOMMENDATIONS_FOR_MULTIPLE_WAREHOUSE,
    GET_R2F_THRESHOLDS,
    GET_R2F_THRESHOLDS_FOR_WAREHOUSE,
    GET_ENTIRE_NETWORK_R2F_THRESHOLDS,
    PUBLISH_R2F_THRESHOLDS,
    DELETE_GSLA,
    DELETE_R2F_THRESHOLDS,
    GET_PC_FOR_WAREHOUSE_LIST,
    GET_CORA_DATA,
    AFTX_GET_FC_CORA_DATA,
    GET_FC_METADATA,
    GET_FC_CONFIG,
    UPDATE_FC_CONFIG,
    TRIGGER_CATEGORIZATION_GENERATION,
    GET_HISTORY_FOR_SHIPMENT,
} from 'src/core/config/APIRoutes';
import { CORAInput, GetCORADataRequest, GetCORADataResponse } from 'src/features/types/coraTypes';
import { UserPermissions } from 'src/features/types/backendApi';
import {
    GSLAApiResponse,
    GSLAPublishRequest,
    GSLAToPublish,
    GetGSLAForWarehouseRequest,
    GetGSLAForWarehouseResponse,
    GetGSLARequest,
    GetGSLAResponse,
    GetGSLARecommendationForWarehouseRequest,
    GetGSLARecommendationForWarehouseResponse,
    GetGSLARecommendationsRequest,
    GetGSLARecommendationsResponse,
    GetR2FThresholdsRequest,
    GetR2FThresholdsResponse,
    GetR2FThresholdsForWarehouseRequest,
    GetR2FThresholdsForWarehouseResponse,
    GetEntireNetworkR2FThresholdsRequest,
    GetEntireNetworkR2FThresholdsResponse,
    PublishR2FThresholdsRequest,
    PublishR2FThresholdsResponse,
    PublishGSLAResponse,
    DeleteGSLARequest,
    DeleteGSLAResponse,
    DeleteR2FThresholdsRequest,
    DeleteR2FThresholdsResponse,
    GetPCForWarehouseListRequest,
    GetPCForWarehouseListResponse,
} from 'src/features/types/gslaApi';
import { GetFCMetadataRequest, GetFCMetadataResponse } from 'src/features/types/fcMetadataTypes';
import { getAPIHostWithPrefix, getBackendAPIHostWithPrefix, getAftxDomain } from 'src/utils/Environment';
import fetch from 'src/utils/APIGatewayClients';
import { getCookiesMap } from './CookiesUtils';
import { isAFTXDomain } from './Auth';

export function getSiteGSLA(warehouseId: string, preserveDefaultTypes?: boolean): Promise<GetGSLAForWarehouseResponse> | undefined
{
    if (!warehouseId) return;
    const requestBody: GetGSLAForWarehouseRequest = {
        warehouseId,
        ...(preserveDefaultTypes !== undefined && { preserveDefaultTypes })
    };

    return fetch(
        `${getAPIHostWithPrefix()}${GET_GSLA_FOR_WAREHOUSE}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        },
    );
}

export function getMultipleSitesGSLA(warehouseIds: string[], preserveDefaultTypes?: boolean): Promise<GetGSLAResponse> | undefined
{
    if (!warehouseIds) return;
    const requestBody: GetGSLARequest = {
        warehouseIds,
        ...(preserveDefaultTypes !== undefined && { preserveDefaultTypes })
    };

    return fetch(
        `${getAPIHostWithPrefix()}${GET_GSLA_FOR_MULTIPLE_WAREHOUSES}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        },
    );
}

export function getSiteGSLARecommendations(warehouseId: string): Promise<GetGSLARecommendationForWarehouseResponse> | undefined
{
    if (!warehouseId) return;
    const requestBody: GetGSLARecommendationForWarehouseRequest = { warehouseId };
    
    return fetch(
        `${getAPIHostWithPrefix()}${GET_GSLA_RECOMMENDATIONS_FOR_WAREHOUSE}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        },
    );
}

export function getMultipleSitesGSLARecommendations(warehouseIds: string[]): Promise<GetGSLARecommendationsResponse> | undefined
{
    if (!warehouseIds) return;
    const requestBody: GetGSLARecommendationsRequest = { warehouseIds };

    return fetch(
        `${getAPIHostWithPrefix()}${GET_GSLA_RECOMMENDATIONS_FOR_MULTIPLE_WAREHOUSE}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        },
    );
}

export async function publishGSLAValues(requestBody: GSLAPublishRequest): Promise<PublishGSLAResponse>
{
    const requestBodyString = JSON.stringify(requestBody);
    
    return fetch(
        `${getAPIHostWithPrefix()}${PUBLISH_GSLA_FOR_WAREHOUSE}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: requestBodyString,
        },
    );
}

export function getUserPermissions(userId: string): Promise<UserPermissions> | undefined
{
    if (isAFTXDomain()) {
        return undefined;
    }

    const requestBody = { userId };

    return fetch(
        `${getBackendAPIHostWithPrefix()}${GET_USER_PERMISSIONS}`,
        {
            method: 'POST',
            body: JSON.stringify(requestBody),
        },
    );
}

export function getR2FThresholds(warehouseIds: string[]): Promise<GetR2FThresholdsResponse> | undefined
{
    if (!warehouseIds) return;
    const requestBody: GetR2FThresholdsRequest = { 
        warehouseIds,
        preserveDefaultTypes: true 
    };

    return fetch(
        `${getAPIHostWithPrefix()}${GET_R2F_THRESHOLDS}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        },
    );
}

export function getR2FThresholdsForWarehouse(warehouseId: string, preserveDefaultTypes: boolean = true): Promise<GetR2FThresholdsForWarehouseResponse> | undefined
{
    if (!warehouseId) return;
    const requestBody: GetR2FThresholdsForWarehouseRequest = { 
        warehouseId,
        preserveDefaultTypes
    };

    return fetch(
        `${getAPIHostWithPrefix()}${GET_R2F_THRESHOLDS_FOR_WAREHOUSE}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        },
    );
}

export function getEntireNetworkR2FThresholds(preserveDefaultTypes: boolean = true): Promise<GetEntireNetworkR2FThresholdsResponse> | undefined
{
    const requestBody: GetEntireNetworkR2FThresholdsRequest = {
        preserveDefaultTypes
    };

    return fetch(
        `${getAPIHostWithPrefix()}${GET_ENTIRE_NETWORK_R2F_THRESHOLDS}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        },
    );
}

export async function publishR2FThresholds(requestBody: PublishR2FThresholdsRequest): Promise<PublishR2FThresholdsResponse>
{
    // Ensure preserveDefaultTypes is set to true
    const updatedRequestBody = {
        ...requestBody,
        preserveDefaultTypes: true
    };

    // Filter out Default_Network_PC changes for non-DEFAULT warehouses
    // This prevents saving Default_Network_PC changes to warehouse-specific configs
    // while still allowing the UI to show Default_Network_PC for visibility
    if (updatedRequestBody.r2fToPublishList) {
        updatedRequestBody.r2fToPublishList.forEach(item => {
            // If we're updating an FC warehouse (not 'DEFAULT')
            if (item.warehouseId && item.warehouseId !== 'DEFAULT') {
                // And if we have a Default_Network_PC in the r2fToPublish
                if (item.r2fToPublish && 'Default_Network_PC' in item.r2fToPublish) {
                    // Remove Default_Network_PC from FC warehouse request
                    // This prevents saving Default_Network_PC changes to warehouse-specific config
                    // The UI restrictions should already prevent these changes from being made
                    delete item.r2fToPublish['Default_Network_PC'];
                }
            }
            // If warehouseId is 'DEFAULT', we allow Default_Network_PC to be saved
        });
    }

    const response = await fetch(
        `${getAPIHostWithPrefix()}${PUBLISH_R2F_THRESHOLDS}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(updatedRequestBody),
        },
    );

    return response;
}

export function deleteGSLA(requestBody: DeleteGSLARequest): Promise<DeleteGSLAResponse> | undefined
{
    return fetch(
        `${getAPIHostWithPrefix()}${DELETE_GSLA}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        },
    );
}

export function deleteR2FThresholds(requestBody: DeleteR2FThresholdsRequest): Promise<DeleteR2FThresholdsResponse> | undefined
{
    return fetch(
        `${getAPIHostWithPrefix()}${DELETE_R2F_THRESHOLDS}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        },
    );
}

export function getPCForWarehouseList(warehouseIds: string[]): Promise<GetPCForWarehouseListResponse> | undefined
{
    if (!warehouseIds || warehouseIds.length === 0) return;
    const requestBody: GetPCForWarehouseListRequest = { warehouseIds };

    return fetch(
        `${getBackendAPIHostWithPrefix()}${GET_PC_FOR_WAREHOUSE_LIST}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        },
    );
}

export function getCORAData(input: CORAInput): Promise<GetCORADataResponse> {
    const requestBody = {
        warehouseIds: input.nodeIds,
        fulfillmentProgram: input.fulfillmentProgram,
        modeSelector: input.modeSelector,
        durationOfRiskWindowInDays: input.durationOfRiskWindowInDays
    };

    return fetch(`${getBackendAPIHostWithPrefix()}${GET_CORA_DATA}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
    }).then(data => {
        const transformedData: GetCORADataResponse = {
            nodeFailures: data.nodeFailures || {},
            riskDataByNode: {}
        };

        if (data.riskDataByNode) {
            Object.keys(data.riskDataByNode).forEach(nodeKey => {
                const nodeData = data.riskDataByNode[nodeKey];
                transformedData.riskDataByNode[nodeKey] = {
                    ...nodeData,
                    nodeId: nodeKey
                };
            });
        }

        return transformedData;
    });
}

export function aftxGetFCCORAData(input: CORAInput): Promise<GetCORADataResponse> {
    const requestBody = {
            getFCCORADataRequest: {
            warehouseIds: input.nodeIds,
            fulfillmentProgram: input.fulfillmentProgram,
            modeSelector: input.modeSelector,
            durationOfRiskWindowInDays: input.durationOfRiskWindowInDays
        },
        cookiesMap: getCookiesMap()
    };

    return fetch(`${getAftxDomain()}${AFTX_GET_FC_CORA_DATA}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
    }).then(data => {
        const transformedData: GetCORADataResponse = {
            nodeFailures: data.nodeFailures || {},
            riskDataByNode: {}
        };

        if (data.riskDataByNode) {
            Object.keys(data.riskDataByNode).forEach(nodeKey => {
                const nodeData = data.riskDataByNode[nodeKey];
                transformedData.riskDataByNode[nodeKey] = {
                    ...nodeData,
                    nodeId: nodeKey
                };
            });
        }

        return transformedData;
    });
}


export function getFCMetadata(warehouseId: string): Promise<GetFCMetadataResponse> | undefined {
    if (!warehouseId) return;
    const requestBody: GetFCMetadataRequest = { warehouseId };

    return fetch(
        `${getAPIHostWithPrefix()}${GET_FC_METADATA}`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody),
        },
    );
}

export interface GetFCConfigResponse {
    fcName: string;
    productionCapabilityMappingType: string;
    categorizationGenerationType: string;
    availableProductionCapabilityMappingTypes?: string[];
    availableCategorizationGenerationTypes?: string[];
}

export function getFCConfig(fcName: string): Promise<GetFCConfigResponse> | undefined {
    if (!fcName) return;
    return fetch(`${getBackendAPIHostWithPrefix()}${GET_FC_CONFIG}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fcName }),
    });
}

export interface UpdateFCConfigRequest {
    fcName: string;
    productionCapabilityMappingType?: string;
    categorizationGenerationType?: string;
    ticketUrl: string;
    updatedBy: string;
}

export interface UpdateFCConfigResponse {
    success: boolean;
    message: string;
    fcName?: string;
    productionCapabilityMappingType?: string;
    categorizationGenerationType?: string;
}

export function updateFCConfig(request: UpdateFCConfigRequest): Promise<UpdateFCConfigResponse> | undefined {
    if (!request.fcName) return;
    return fetch(`${getBackendAPIHostWithPrefix()}${UPDATE_FC_CONFIG}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
    });
}

export interface TriggerCategorizationGenerationResponse {
    success: boolean;
    message: string;
}

export function triggerCategorizationGeneration(fcName: string, type?: string): Promise<TriggerCategorizationGenerationResponse> | undefined {
    if (!fcName) return;
    return fetch(`${getBackendAPIHostWithPrefix()}${TRIGGER_CATEGORIZATION_GENERATION}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fcName, type: type ?? 'PICKING' }),
    }).then(res => {
        if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
        return res.json();
    });
}

// Shipment History queries
export interface GetHistoryForShipmentRequest
{
    shipmentId: string;
}

export interface ShipmentDetails
{
    [attributeName: string]: string;
}

export interface GetHistoryForShipmentResponse
{
    historyAttributesByShipment: {
        [shipmentId: string]: ShipmentDetails[];
    };
}

export function getHistoryForShipment( shipmentId: string ): Promise<GetHistoryForShipmentResponse> | undefined
{
    if ( !shipmentId ) return;

    const requestBody: GetHistoryForShipmentRequest = { shipmentId };

    return fetch(
        `${getBackendAPIHostWithPrefix()}${GET_HISTORY_FOR_SHIPMENT}`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify( requestBody ),
        },
    );
}